import React, { useEffect, useMemo, useState } from 'react';
import { FileText, Search } from 'lucide-react';
import { bookingApi } from '../../api/bookingApi';
import { ClientLayout } from '../../components/layouts';
import { openInvoicePrint } from '../../utils/invoicePrint';
import { formatClientDate, formatClientMoney } from './clientHelpers';

const ClientInvoices = () => {
  const [bookings, setBookings] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [invoiceLoading, setInvoiceLoading] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    bookingApi.myBookings()
      .then((response) => { if (!cancelled) setBookings(response.data || []); })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.message || "Couldn't load your invoices."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const invoices = useMemo(() => {
    const q = query.trim().toLowerCase();
    return bookings.filter((booking) => {
      if (!booking.invoiceAvailable) return false;
      const haystack = `${booking.service?.name || ''} ${booking.gigger?.name || ''} ${booking.trackingId || ''}`.toLowerCase();
      return !q || haystack.includes(q);
    });
  }, [bookings, query]);

  const openInvoice = async (booking) => {
    setInvoiceLoading(booking._id);
    setError('');
    try {
      const response = await bookingApi.clientInvoice(booking._id);
      openInvoicePrint({ invoice: response.data, kind: 'client', booking });
    } catch (err) {
      setError(err.response?.data?.message || "Couldn't load your invoice.");
    } finally {
      setInvoiceLoading('');
    }
  };

  return (
    <ClientLayout title="Invoices">
      <div className="mx-auto w-full max-w-[1200px]">
        <div className="mb-6">
          <h2 className="m-0 font-nunito text-[clamp(1.6rem,3vw,2.15rem)] font-black text-giggs-ink">Invoices & receipts</h2>
          <p className="mb-0 mt-2 text-sm leading-6 text-giggs-muted">View and print invoices generated for your completed or billable bookings.</p>
        </div>

        <label className="relative mb-5 block w-full max-w-md">
          <Search size={17} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search invoice by service or tracking ID" className="box-border w-full rounded-xl border-0 bg-white py-3 pl-10 pr-4 text-sm text-giggs-ink shadow-sm ring-1 ring-inset ring-slate-200 outline-none focus:ring-2 focus:ring-giggs-green" />
        </label>

        {error && <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">{error}</div>}

        <div className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200/80">
          {loading ? <div className="px-5 py-14 text-center text-sm text-giggs-muted">Loading invoices…</div> : invoices.length === 0 ? (
            <div className="flex flex-col items-center px-5 py-14 text-center"><span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400"><FileText size={21} /></span><h3 className="mb-0 mt-4 text-sm font-bold text-giggs-ink">No invoices available</h3><p className="mb-0 mt-1 text-xs text-giggs-muted">Invoices will appear here when they become available.</p></div>
          ) : (
            <div className="divide-y divide-slate-100">
              {invoices.map((booking) => (
                <article key={booking._id} className="flex flex-col gap-4 px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                  <div className="flex min-w-0 items-start gap-3.5">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-giggs-green/10 text-giggs-green"><FileText size={19} /></span>
                    <div className="min-w-0"><h3 className="m-0 truncate text-sm font-bold text-giggs-ink">{booking.service?.name || 'Giggs invoice'}</h3><p className="mb-0 mt-1 text-xs text-giggs-muted">{formatClientDate(booking.scheduledDate)} · {booking.gigger?.name || 'Gigger'}</p><p className="mb-0 mt-1 break-all font-mono text-[11px] text-slate-400">{booking.trackingId}</p></div>
                  </div>
                  <div className="flex items-center justify-between gap-4 sm:justify-end"><span className="font-nunito text-lg font-black text-giggs-ink">{formatClientMoney(booking.total)}</span><button type="button" disabled={invoiceLoading === booking._id} onClick={() => openInvoice(booking)} className="cursor-pointer rounded-xl border border-giggs-green/20 bg-giggs-green px-4 py-2.5 text-xs font-bold text-white transition hover:bg-giggs-green-hover disabled:cursor-not-allowed disabled:opacity-50">{invoiceLoading === booking._id ? 'Loading…' : 'View invoice'}</button></div>
                </article>
              ))}
            </div>
          )}
        </div>
      </div>
    </ClientLayout>
  );
};

export default ClientInvoices;
